import { useState } from 'react';
import {
  ButtonAsync,
  Center,
  Icons,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  VStack,
} from '@tonic/central-specialties-ui-themed';
import {
  Button,
  ButtonIcon,
  ButtonText,
  HStack,
  Icon,
  Pressable,
  Text,
} from '@gluestack-ui/themed';
import { UserForm } from './UserForm.tsx';
import { Schemas } from '@tonic/central-specialties-utils';
import moment from 'moment';
import ReactivationDatePickerModal from '../../../ReactivationDatePickerModal.tsx';

interface DisabledUserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  goBack: () => void;
  disabledUser: Schemas['UserModel'];
  onReinstateSuccess?: () => void;
}

export const DisabledUserFormModal = ({
  isOpen,
  onClose,
  goBack,
  disabledUser,
  onReinstateSuccess,
}: DisabledUserFormModalProps) => {
  const [reactivationDate, setReactivationDate] = useState<string>(
    moment().format('YYYY-MM-DD'),
  );
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalBackdrop />
        <ModalContent
          borderRadius={0}
          maxHeight="80%"
          w="$96"
          width="60%"
          maxWidth={1000}
          minWidth={500}
        >
          <ModalHeader
            bg="$primary700"
            alignItems="center"
            justifyContent="space-between"
            py="$2"
            pr="$6"
          >
            <HStack
              w="$full"
              h="$full"
              justifyContent="space-between"
              alignItems="center"
            >
              <HStack flex={1}>
                <Button gap="$2" onPress={goBack}>
                  <ButtonIcon as={Icons.ChevronLeft} />
                  <ButtonText>Back</ButtonText>
                </Button>
              </HStack>
              <Center flex={1}>
                <Text color="$white" bold size="lg">
                  {disabledUser.fullName}
                </Text>
              </Center>
              <HStack flex={1} justifyContent="flex-end">
                <ModalCloseButton
                  w="$8"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Icon color="$white" size="xl" as={Icons.X} />
                </ModalCloseButton>
              </HStack>
            </HStack>
          </ModalHeader>
          <UserForm
            user={disabledUser}
            onSuccess={onReinstateSuccess}
            reactivationDate={reactivationDate}
            formProps={{
              Container: ({ children }) => (
                <ModalBody w="$full">
                  <Center flex={1} px="$32" py="$10">
                    <VStack w="$full" maxWidth={600} minWidth={300} space="xl">
                      {children}
                    </VStack>
                  </Center>
                </ModalBody>
              ),
              SubmitButton: ({ submit, pending, disabled }) => (
                <ModalFooter bg="$white" hardShadow={2}>
                  <VStack w="$full" space="md">
                    <Pressable onPress={() => setDatePickerVisible(true)}>
                      <HStack
                        alignItems="center"
                        justifyContent="space-between"
                        borderWidth={1}
                        borderColor="$borderLight300"
                        borderRadius="$sm"
                        px="$3"
                        py="$2"
                      >
                        <Text size="sm" color="$textLight600">
                          Reactivation date
                        </Text>
                        <HStack alignItems="center" space="sm">
                          <Text size="sm" fontWeight="$medium">
                            {moment(reactivationDate).format('MMM D, YYYY')}
                          </Text>
                          <Icon
                            as={Icons.Calendar}
                            size="sm"
                            color="$textLight600"
                          />
                        </HStack>
                      </HStack>
                    </Pressable>
                    <HStack w="$full" space="2xl" justifyContent="flex-end">
                      <Button
                        size="lg"
                        w="$32"
                        variant="outline"
                        onPress={onClose}
                      >
                        <ButtonText>Cancel</ButtonText>
                      </Button>
                      <ButtonAsync
                        size="lg"
                        w="$32"
                        variant="solid"
                        onPress={submit}
                        pending={pending}
                        isDisabled={disabled}
                      >
                        <ButtonText>Reinstate user</ButtonText>
                      </ButtonAsync>
                    </HStack>
                  </VStack>
                </ModalFooter>
              ),
            }}
          />
        </ModalContent>
      </Modal>
      <ReactivationDatePickerModal
        visible={datePickerVisible}
        onClose={() => setDatePickerVisible(false)}
        onDateSelect={(date) => {
          setReactivationDate(date);
          setDatePickerVisible(false);
        }}
      />
    </>
  );
};
